import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill, FlaskConical, CreditCard, ChevronRight, CalendarClock } from "lucide-react";

export default function PatientHome() {
  const { user } = useAuth();
  const [renewal, setRenewal] = useState(null);
  const [plan, setPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/patient/renewal").then((r) => setRenewal(r.data)).catch(() => {});
    api.get("/patient/treatment-plan").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const firstName = (user?.name || "there").split(" ")[0];
  const dueSoon = renewal && renewal.days_until != null && renewal.days_until <= (renewal.reminder_days || 5);

  return (
    <div className="space-y-6" data-testid="patient-home">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Hi {firstName} 👋</h1>
        <p className="text-sm text-muted-foreground mt-1">Here's your care, organized.</p>
      </div>

      {/* Renewal nudge */}
      {renewal && (
        <div
          data-testid="home-renewal-card"
          className={`rounded-xl border p-5 ${dueSoon ? "bg-amber-50 border-amber-200" : "bg-card border-border"}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{renewal.membership_plan}</p>
              <p className="font-heading text-lg font-bold mt-1">
                {renewal.days_until < 0
                  ? `Renewal was due ${Math.abs(renewal.days_until)} days ago`
                  : renewal.days_until === 0
                  ? "Renews today"
                  : `Renews in ${renewal.days_until} days`}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">${renewal.price} · {renewal.renewal_date}</p>
            </div>
            <CalendarClock className={`w-6 h-6 ${dueSoon ? "text-amber-600" : "text-muted-foreground"}`} />
          </div>
          <button
            data-testid="home-manage-renewal"
            onClick={() => navigate("/portal/membership")}
            className="mt-4 inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90"
          >
            Review my options <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Current plan */}
      {plan?.current && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="home-plan-card">
          <div className="flex items-center gap-2 mb-3">
            <Pill className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-base font-semibold">Your Treatment Plan</h3>
          </div>
          <p className="font-medium">{plan.current.medication}{plan.current.dose ? ` · ${plan.current.dose}` : ""}</p>
          {plan.current.frequency && <p className="text-sm text-muted-foreground">{plan.current.frequency}</p>}
          <button onClick={() => navigate("/portal/treatment")} className="text-sm text-muted-foreground hover:text-foreground mt-3 inline-flex items-center gap-1">
            View details <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-4">
        <QuickLink icon={FlaskConical} label="My Labs" onClick={() => navigate("/portal/labs")} testid="home-link-labs" />
        <QuickLink icon={CreditCard} label="Membership" onClick={() => navigate("/portal/membership")} testid="home-link-membership" />
      </div>
    </div>
  );
}

function QuickLink({ icon: Icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="bg-card border border-border rounded-xl p-5 flex items-center gap-3 hover:shadow-sm hover:-translate-y-[1px] transition-all text-left">
      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
      <span className="font-medium">{label}</span>
    </button>
  );
}
